import React from "react";
import { NewConnectionFeeScripting } from "./types";
import { Stack, Text, TextLink } from "@origin-digital/ods-core";

const mapDBNameFromId = (id: string | undefined): string => {
  if (!id) return "Unknown Distributor";
  switch (id) {
    case "ENERGYAP":
      return "Ausgrid";
    case "INTEGP":
      return "Endeavour";
    case "ENERGEXP":
      return "Energex";
    case "CNRGYP":
      return "Essential";
    case "ACTEWP":
      return "Evoenergy";
    case "UMPLP":
      return "SAPN";
    case "EASTERN":
      return "AusNet Services";
    case "CITIPP":
      return "CitiPower";
    case "SOLARISP":
      return "Jemena";
    case "POWCP":
      return "Powercor";
    case "UNITED":
      return "United Energy";
    case "ACTEWNWO":
      return "EVOEnergy";
    case "ENVESTRA":
      return "Australian Gas Network";
    case "CRPNWO":
      return "Australian Gas Network";
    case "COUNTRYNWO":
      return "Australian Gas Network";
    case "ENVSA":
      return "Australian Gas Network";
    case "ENVQLD":
      return "Australian Gas Network";
    case "APTALLGAS":
      return "Allgas Energy";
    case "TXUN":
      return "Ausnet Gas";
    case "AGLGNNWO":
      return "Jemena Gas";
    case "MULTINET":
      return "Multinet";
    // Legacy support
    default:
      return id; // Return the ID if no mapping found
  }
};

type FuelType = "electricity" | "gas";

const FeesByFuelType = ({
  feesByFuelType,
  fuelType,
}: {
  feesByFuelType: NewConnectionFeeScripting[];
  fuelType: FuelType;
}) => {
  if (feesByFuelType.length === 0) {
    return null;
  }

  const getFeeScripting = (fee: NewConnectionFeeScripting) => {
    const feeScripting = fee.fee_scripting;
    const phaseType = fee.is_single_phase ? "single" : "multi";
    const price = fee.price;
    const DBName = mapDBNameFromId(fee.lnsp_ids?.[0]);
    const isMultiDB = fee.is_multi_db;

    if (isMultiDB && !!price && fuelType === "electricity") {
      return (
        <Stack space="medium">
          <Text variant="body-small">
            For a {phaseType}-phase electricity connection, the connection fee
            may be up to ${price} (incl GST) depending on your distribution
            area. Additional fees may apply if work cannot be completed due to
            site or access issues.
          </Text>
          <Text variant="body-small">
            Please note any fees that are charged by the distributor will be
            passed on by us. These fees are current as at 1 July 2025 and do
            change from time to time. You can find more details on these fees at{" "}
            <TextLink
              href="https://originenergy.com.au/meterfees"
              target="_blank"
            >
              originenergy.com.au/meterfees
            </TextLink>
            .
          </Text>
        </Stack>
      );
    }
    if (isMultiDB && !!price && fuelType === "gas") {
      return (
        <Stack space="medium">
          <Text variant="body-small">
            For a gas connection, the connection fee may be up to ${price} (incl
            GST) depending on your distribution area. Additional fees may apply
            if work cannot be completed due to site or access issues.
          </Text>
          <Text variant="body-small">
            Please note any fees that are charged by the distributor will be
            passed on by us. These fees are current as at 1 July 2025 and do
            change from time to time. You can find more details on these fees at{" "}
            <TextLink
              href="https://originenergy.com.au/meterfees"
              target="_blank"
            >
              originenergy.com.au/meterfees
            </TextLink>
            .
          </Text>
        </Stack>
      );
    }

    if (feeScripting === "NO_FEE_ELEC") {
      return (
        <Stack space="medium">
          <Text variant="body-small">
            The distributor may charge fees for the connection and they'll pass
            on any fees to the customer directly or to us. If they charge us,
            we'll pass on the fees to you on your bill. Additional fees may
            apply if work cannot be completed due to site or access issues.{" "}
          </Text>
          <Text variant="body-small">
            These fees are current as at 1 July 2025 and do change from time to
            time. You can find more details on these fees at{" "}
            <TextLink
              href="https://originenergy.com.au/meterfees"
              target="_blank"
            >
              originenergy.com.au/meterfees
            </TextLink>
            .
          </Text>
        </Stack>
      );
    }

    if (feeScripting === "FEE_ELEC") {
      return (
        <Stack space="medium">
          <Text variant="body-small">
            For a {phaseType}-phase electricity connection, in the {DBName}{" "}
            distribution area the fee is ${price} (incl GST). Additional fees
            may apply if work cannot be completed due to site or access issues.
          </Text>
          <Text variant="body-small">
            Please note any fees that are charged by the distributor will be
            passed on by us. These fees are current as at 1 July 2025 and do
            change from time to time. You can find more details on these fees at{" "}
            <TextLink
              href="https://originenergy.com.au/meterfees"
              target="_blank"
            >
              originenergy.com.au/meterfees
            </TextLink>
            .
          </Text>
        </Stack>
      );
    }

    if (feeScripting === "QUOTE_OTHERSTATES_GAS") {
      return (
        <Stack space="medium">
          <Text variant="body-small">
            For a gas connection, if a fee applies, this will be quoted to us by
            your distributor and we'll let you know before the work goes ahead.
          </Text>
          <Text variant="body-small">
            Please note any fees that are charged by the distributor will be
            passed on by us. These fees are current as at 1 July 2025 and do
            change from time to time. You can find more details on these fees at{" "}
            <TextLink
              href="https://originenergy.com.au/meterfees"
              target="_blank"
            >
              originenergy.com.au/meterfees
            </TextLink>
            .
          </Text>
        </Stack>
      );
    }

    if (feeScripting === "SERVICE_LINE_VIC") {
      return (
        <Stack space="medium">
          <Text variant="body-small">
            For a standard gas line connection in the {DBName} distribution
            area, the fee is ${price} (incl GST). For a non-standard gas line
            connection, if a fee applies, this will be quoted to us by your
            distributor and we'll let you know before the work goes ahead.
          </Text>
          <Text variant="body-small">
            Please note any fees that are charged by the distributor will be
            passed on by us. These fees are current as at 1 July 2025 and do
            change from time to time. You can find more details on these fees at{" "}
            <TextLink
              href="https://originenergy.com.au/meterfees"
              target="_blank"
            >
              originenergy.com.au/meterfees
            </TextLink>
            .
          </Text>
        </Stack>
      );
    }

    if (feeScripting === "METER_NO_FEE") {
      return (
        <Stack space="medium">
          <Text variant="body-small">
            For a gas meter connection, if a fee applies, this will be quoted to
            us by your distributor and we'll let you know before the work goes
            ahead. Additional fees may apply if work cannot be completed due to
            site or access issues.
          </Text>
          <Text variant="body-small">
            Please note any fees that are charged by the distributor will be
            passed on by us. These fees are current as at 1 July 2025 and do
            change from time to time. You can find more details on these fees at{" "}
            <TextLink
              href="https://originenergy.com.au/meterfees"
              target="_blank"
            >
              originenergy.com.au/meterfees
            </TextLink>
            .
          </Text>
        </Stack>
      );
    }

    if (feeScripting === "METER_FEE") {
      return (
        <Stack space="medium">
          <Text variant="body-small">
            For a gas meter connection, in the {DBName} distribution area, the
            fee is ${price} (incl GST). Additional fees may apply if work cannot
            be completed due to site or access issues.
          </Text>
          <Text variant="body-small">
            Please note any fees that are charged by the distributor will be
            passed on by us. These fees are current as at 1 July 2025 and do
            change from time to time. You can find more details on these fees at{" "}
            <TextLink
              href="https://originenergy.com.au/meterfees"
              target="_blank"
            >
              originenergy.com.au/meterfees
            </TextLink>
            .
          </Text>
        </Stack>
      );
    }

    return null;
  };

  return (
    <Stack space="medium">
      {feesByFuelType.map((fee: NewConnectionFeeScripting) =>
        getFeeScripting(fee)
      )}
    </Stack>
  );
};

export default FeesByFuelType;
